import { useNewVisitNotificationQuery } from './hooks/useNewVisitNotificationQuery';
import { MenuItem, MenuItemProps, Skeleton } from '@higo/ui';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import { MouseEvent, useCallback } from 'react';
import { getDashboardChildRoute, routes } from 'routes';
import { useNavigate } from 'react-router-dom';
import { useVisitsStoreActions } from 'store/visitsStore';

export type HeaderNewVisitsNotificationsMenuItemProps = {
  visitId: number;
  onClose?: () => void;
};

const StyledLink = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.26rem;
  padding-left: 1rem;
`;

const ExaminationSkeleton = styled(Skeleton)`
  height: 2rem;
`;

const DateSkeleton = styled(Skeleton)`
  height: 1rem;
`;

const Message = styled.div(
  ({ theme }) => css`
    ${theme.typography.subtitle1};
    color: ${theme.palette.secondary['900']};
    font-weight: 700;
  `,
);

const MessageDate = styled.div(
  () => css`
    font-weight: 400;
  `,
);

export const HeaderNewVisitsNotificationsMenuItem = ({
  visitId,
  onClose,
  ...props
}: HeaderNewVisitsNotificationsMenuItemProps & MenuItemProps) => {
  const { isLoading, data, dataUpdatedAt } =
    useNewVisitNotificationQuery(visitId);

  const navigate = useNavigate();
  const { removeNewVisit } = useVisitsStoreActions();

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      removeNewVisit(visitId);
      onClose?.();
      navigate(getDashboardChildRoute(routes.dashboard.visits));
    },
    [navigate, removeNewVisit, visitId, onClose],
  );

  return (
    <MenuItem {...props}>
      <StyledLink onClick={handleClick}>
        {isLoading && (
          <>
            <ExaminationSkeleton />
            <DateSkeleton />
          </>
        )}
        {!isLoading && data && (
          <>
            <Message>
              <FormattedMessage
                id="header.notifications.visits.newVisitMessage"
                values={{
                  fullName: `${data.patientFirstName} ${data.patientLastName}`,
                }}
              />
            </Message>
            <MessageDate>{format(dataUpdatedAt, 'HH:mm')}</MessageDate>
          </>
        )}
      </StyledLink>
    </MenuItem>
  );
};
